import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import Seo from "@/components/Seo";
import { FadeUp } from "@/components/Reveal";
import { LuxeButton } from "@/components/shared";
import { ArrowLeft, BedDouble, Bath, Ruler, Calendar, Home, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const photosOf = (l) => {
  const im = l?.image;
  if (!im) return [];
  if (typeof im === "string") return [im];
  return Object.keys(im)
    .filter((k) => !isNaN(Number(k)))
    .sort((a, b) => Number(a) - Number(b))
    .map((k) => im[k])
    .filter((v) => v && typeof v === "object")
    .map((v) => v.url || v.urls?.hires?.url || v.sizes?.thumb)
    .filter(Boolean)
    .map((u) => (u.startsWith("//") ? `https:${u}` : u));
};

const Stat = ({ icon: Icon, label, value }) =>
  value ? (
    <div className="flex flex-col items-center gap-2 border border-warm-border bg-white px-4 py-6 text-center">
      <Icon size={20} strokeWidth={1.25} className="text-champagne" aria-hidden="true" />
      <span className="font-serif text-2xl font-normal text-charcoal">{value}</span>
      <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-muted">{label}</span>
    </div>
  ) : null;

const ListingDetail = () => {
  const { listingId } = useParams();
  const [listing, setListing] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);

  useEffect(() => {
    setListing(null);
    setNotFound(false);
    setPhotoIdx(0);
    axios
      .get(`${API}/idx/listing/${listingId}`)
      .then((r) => setListing(r.data))
      .catch(() => setNotFound(true));
  }, [listingId]);

  if (notFound)
    return (
      <div className="container-luxe flex min-h-[70vh] flex-col items-start justify-center pt-32" data-testid="listing-not-found">
        <h1 className="font-serif text-5xl font-normal text-charcoal">Listing Not Found</h1>
        <p className="mt-4 text-slate-muted">This property may have just sold or been removed from the market.</p>
        <div className="mt-8">
          <LuxeButton to="/listings" variant="dark" testId="listing-back-btn">Browse Current Listings</LuxeButton>
        </div>
      </div>
    );

  if (!listing)
    return (
      <div className="container-luxe flex min-h-[70vh] items-center pt-32" data-testid="listing-loading">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-slate-muted">Loading property…</p>
      </div>
    );

  const photos = photosOf(listing);
  const city = [listing.cityName, listing.stateAbrv || listing.state, listing.zipcode].filter(Boolean).join(", ");
  const statusLabel = listing.propStatus === "ACTIVE" ? "Active" : listing.propStatus === "CLOSED" ? "Sold" : listing.propStatus;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    name: `${listing.address}, ${city}`,
    description: (listing.remarksConcat || "").slice(0, 300),
    image: photos[0],
    url: typeof window !== "undefined" ? window.location.href : undefined,
    offers: listing.price
      ? { "@type": "Offer", price: listing.price, priceCurrency: "USD", availability: listing.propStatus === "ACTIVE" ? "https://schema.org/InStock" : "https://schema.org/SoldOut" }
      : undefined,
  };

  return (
    <>
      <Seo
        title={`${listing.address}, ${city} — ${listing.listingPrice} | Scherif Real Estate`}
        siteName="Scherif Real Estate"
        description={(listing.remarksConcat || `View ${listing.address}, ${city} — presented by Scherif Real Estate.`).slice(0, 155)}
        image={photos[0]}
        jsonLd={jsonLd}
      />

      <article className="pb-24 pt-28 sm:pt-32" data-testid="listing-detail">
        <div className="container-luxe">
          <nav aria-label="Breadcrumb" className="mb-8">
            <Link to="/listings" data-testid="listing-breadcrumb" className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-muted transition-colors hover:text-charcoal">
              <ArrowLeft size={12} strokeWidth={1.5} aria-hidden="true" /> Back to Listings
            </Link>
          </nav>

          {/* Gallery */}
          <FadeUp>
            <div className="relative border border-warm-border bg-white p-2" data-testid="listing-gallery">
              <div className="relative overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={photoIdx}
                    src={photos[photoIdx]}
                    alt={`${listing.address} — photo ${photoIdx + 1}`}
                    className="aspect-[16/10] w-full object-cover"
                    initial={{ opacity: 0, scale: 1.02 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                  />
                </AnimatePresence>
                <span className="absolute left-0 top-5 bg-charcoal px-4 py-2 font-mono text-[9px] uppercase tracking-[0.3em] text-ivory" data-testid="listing-status">
                  {statusLabel}
                </span>
                <span className="absolute bottom-4 right-4 bg-obsidian/70 px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] text-ivory backdrop-blur-sm">
                  {photoIdx + 1} / {photos.length}
                </span>
              </div>
              {photos.length > 1 && (
                <div className="mt-2 flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Property photos">
                  {photos.map((p, i) => (
                    <button
                      key={p + i}
                      onClick={() => setPhotoIdx(i)}
                      aria-label={`View photo ${i + 1}`}
                      aria-selected={i === photoIdx}
                      role="tab"
                      data-testid={`listing-thumb-${i + 1}`}
                      className={`h-16 w-24 shrink-0 overflow-hidden border-2 transition-all duration-300 ${i === photoIdx ? "border-champagne" : "border-transparent opacity-60 hover:opacity-100"}`}
                    >
                      <img src={p} alt="" className="h-full w-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </FadeUp>

          {/* Header */}
          <div className="mt-12 grid gap-10 lg:grid-cols-12">
            <FadeUp className="lg:col-span-8">
              <p className="font-serif text-5xl font-normal tracking-tight text-charcoal sm:text-6xl" data-testid="listing-price">
                {listing.listingPrice}
              </p>
              <h1 className="mt-4 font-serif text-3xl font-normal leading-snug text-charcoal sm:text-4xl" data-testid="listing-address">
                {listing.address}
              </h1>
              <p className="mt-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-muted">
                <MapPin size={12} strokeWidth={1.5} aria-hidden="true" /> {city}
              </p>
            </FadeUp>
            <FadeUp delay={0.1} className="lg:col-span-4">
              <div className="border border-warm-border bg-stone-beige p-7">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-champagne">Presented by</p>
                <p className="mt-3 font-serif text-2xl font-normal text-charcoal">Sobeida “Soby” Scherif</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-muted">Scherif Real Estate</p>
                <a href="tel:+12403724804" data-testid="listing-call" className="mt-5 block font-serif text-xl text-charcoal transition-colors hover:text-champagne">
                  240-372-4804
                </a>
                <div className="mt-6 flex flex-col gap-3">
                  <LuxeButton to="/contact" variant="dark" testId="listing-tour-btn" className="w-full">Schedule a Tour</LuxeButton>
                </div>
              </div>
            </FadeUp>
          </div>

          {/* Key stats */}
          <FadeUp className="mt-14">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="listing-stats">
              <Stat icon={BedDouble} label="Bedrooms" value={listing.bedrooms} />
              <Stat icon={Bath} label="Bathrooms" value={listing.totalBaths || listing.fullBaths} />
              <Stat icon={Ruler} label="Sq Ft" value={listing.sqFt} />
              <Stat icon={Calendar} label="Year Built" value={listing.yearBuilt} />
              <Stat icon={Home} label="Lot (Acres)" value={listing.acres} />
              <Stat icon={Ruler} label="$ / Sq Ft" value={listing.pricePerSqFt ? `$${listing.pricePerSqFt}` : null} />
            </div>
          </FadeUp>

          {/* Description + details */}
          <div className="mt-16 grid gap-14 lg:grid-cols-12">
            <FadeUp className="lg:col-span-8">
              <h2 className="font-serif text-3xl font-normal text-charcoal">About This Home</h2>
              <div className="mt-6 space-y-5 text-base leading-[1.9] text-charcoal/85" data-testid="listing-description">
                {(listing.remarksConcat || "").split(/\n+/).filter(Boolean).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </FadeUp>
            <FadeUp delay={0.1} className="lg:col-span-4">
              <h2 className="font-serif text-3xl font-normal text-charcoal">Details</h2>
              <dl className="mt-6 divide-y divide-warm-border border-y border-warm-border" data-testid="listing-facts">
                {[
                  ["Property Type", listing.idxPropType || listing.propType],
                  ["Subdivision", listing.subdivision],
                  ["County", listing.countyName],
                  ["Style", listing.advanced?.architecturalStyle],
                  ["Full Baths", listing.fullBaths],
                  ["Half Baths", listing.halfBaths],
                  ["Zoning", listing.zoning],
                  ["MLS #", listing.listingID],
                ]
                  .filter(([, v]) => v)
                  .map(([k, v]) => (
                    <div key={k} className="flex items-baseline justify-between gap-4 py-3.5">
                      <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-muted">{k}</dt>
                      <dd className="text-right text-sm text-charcoal">{v}</dd>
                    </div>
                  ))}
              </dl>
            </FadeUp>
          </div>

          <p className="mt-16 border-t border-warm-border pt-6 text-[11px] leading-relaxed text-slate-muted/80">
            Listing data provided by Bright MLS via IDX. Information is deemed reliable but not guaranteed and is provided exclusively for
            consumers&apos; personal, non-commercial use. Listing broker: Scherif Real Estate.
          </p>
        </div>
      </article>
    </>
  );
};

export default ListingDetail;
